/** Range of a random particle value (min … max). */
export interface ValueRange {
  readonly min: number;
  readonly max: number;
}

/**
 * Shine of the golden bottle (SEG_GOLDEN_RAMP_60M, collectibles/GoldenShinePresenter): a pulsing golden halo and
 * a turning star glint (added light) – unlit billboards outside the fog, so the bottle is seen from far away –
 * and a few sparkles rising around it; on pickup the halo flares out in a sparkle burst. Lengths are in
 * units of the bottle at scale 1 – the effect grows with the placed bottle (2.4× on the ramp).
 * Cheap on phones: two quads and one small particle system, created once (one golden bottle at a time – the
 * schedule marks lie at least 500 m apart).
 */
export interface GoldenShineConfig {
  /** Glow centre above the bottle's base – the middle of the bottle. */
  readonly centerHeight: number;
  readonly color: string;
  readonly halo: {
    readonly size: number;
    /** Breathing of the halo: pulses per second, ± share of its size, opacity range. */
    readonly pulseHz: number;
    readonly pulse: number;
    readonly opacity: ValueRange;
  };
  readonly glint: {
    readonly size: number;
    readonly rays: number;
    readonly degreesPerSecond: number;
    readonly opacity: number;
    /** A bright twinkle every `flashPeriod` s, fading over `flashSeconds` (to `flashBoost` × opacity). */
    readonly flashPeriod: number;
    readonly flashSeconds: number;
    readonly flashBoost: number;
  };
  readonly sparkles: {
    readonly capacity: number;
    /** Particles per second around the bottle. */
    readonly rate: number;
    /** Emitter box around the glow centre (half width, half height). */
    readonly area: { readonly halfWidth: number; readonly halfHeight: number };
    readonly size: ValueRange;
    readonly lifetime: ValueRange;
    readonly speed: ValueRange;
    /** Upward drift (units/s²). */
    readonly rise: number;
    /** Pickup: particles thrown out at once, and how fast. */
    readonly burst: number;
    readonly burstSpeed: ValueRange;
  };
  /**
   * Seen from far, halo and glint grow – linearly from 1 at `from` m camera distance to `scale` at `to` m
   * and beyond: a bright mark down the straight that does not flood the screen up close.
   */
  readonly farBoost: { readonly from: number; readonly to: number; readonly scale: number };
  /** Pickup: the halo flares up to `scale` × its size and fades out over `seconds`. */
  readonly pickupFlare: { readonly seconds: number; readonly scale: number };
}

/** Feedback intensities (camera trauma 0..1, toast durations in seconds) and particle look. */
export interface EffectsConfig {
  readonly hitTrauma: number;
  readonly stumbleTrauma: number;
  readonly doubleJumpTrauma: number;
  readonly toastSeconds: number;
  /** Hidden pause between two notices – a little longer than the fade in ui.css (.hud-toast transition). */
  readonly toastGapSeconds: number;
  /** Info notices waiting at most behind the current one – further ones are dropped rather than shown late. */
  readonly toastMaxPending: number;
  /** "GOLDENE FLASCHE!" notice after the golden bottle – a little longer than the others. */
  readonly goldenToastSeconds: number;
  readonly goldenShine: GoldenShineConfig;
  /** Dust kicked up while sliding and when landing (site gravel, light brown-grey). */
  readonly dust: {
    readonly capacity: number;
    /** Particles per second while sliding. */
    readonly slideRate: number;
    /** Puff when a slide starts. */
    readonly slideBurst: number;
    /** Puff when landing faster than `landMinImpact` (m/s). */
    readonly landBurst: number;
    readonly landMinImpact: number;
    /** Emitter box around the feet (m, relative to the player root). */
    readonly area: { readonly halfWidth: number; readonly height: number; readonly halfDepth: number };
    readonly size: { readonly min: number; readonly max: number; readonly growth: number };
    readonly lifetime: { readonly min: number; readonly max: number };
    /** Initial speed (m/s) and upward drift (m/s²). */
    readonly speed: { readonly min: number; readonly max: number };
    readonly rise: number;
    /** RGBA colours: fresh and fading. */
    readonly color: readonly [number, number, number, number];
    readonly colorFade: readonly [number, number, number, number];
  };
}

export const EFFECTS_CONFIG: EffectsConfig = {
  hitTrauma: 0.85,
  stumbleTrauma: 0.45,
  doubleJumpTrauma: 0.08,
  toastSeconds: 1.6,
  toastGapSeconds: 0.25,
  toastMaxPending: 3,
  goldenToastSeconds: 2.4,
  goldenShine: {
    centerHeight: 0.4,
    color: '#ffc93a',
    halo: { size: 2.6, pulseHz: 1.1, pulse: 0.12, opacity: { min: 0.5, max: 0.8 } },
    glint: { size: 2.6, rays: 4, degreesPerSecond: 24, opacity: 0.75, flashPeriod: 1.7, flashSeconds: 0.35, flashBoost: 1.6 },
    sparkles: {
      capacity: 48,
      rate: 24,
      area: { halfWidth: 0.45, halfHeight: 0.45 },
      size: { min: 0.05, max: 0.13 },
      lifetime: { min: 0.7, max: 1.3 },
      speed: { min: 0.1, max: 0.35 },
      rise: 0.25,
      burst: 60,
      burstSpeed: { min: 1.2, max: 2.6 },
    },
    farBoost: { from: 30, to: 90, scale: 2 },
    pickupFlare: { seconds: 0.35, scale: 2.2 },
  },
  dust: {
    capacity: 140,
    slideRate: 45,
    slideBurst: 16,
    landBurst: 12,
    landMinImpact: 6,
    area: { halfWidth: 0.3, height: 0.08, halfDepth: 0.35 },
    size: { min: 0.25, max: 0.5, growth: 1.8 },
    lifetime: { min: 0.3, max: 0.55 },
    speed: { min: 0.4, max: 1.0 },
    rise: 0.2,
    color: [0.55, 0.48, 0.4, 0.55],
    colorFade: [0.58, 0.53, 0.47, 0],
  },
};
